import axios from 'axios';

// Create axios instance with base configuration
// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_BASE_URL = 'https://hotel-inventary-ai-based.vercel.app';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to attach tokens
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    if (error.response?.status === 401) {
      // Clear token and redirect to login
      localStorage.removeItem('token');
      window.location.href = '/login';
    } else if (error.response?.status === 402) {
      // Redirect to pricing on insufficient credits
      window.location.href = '/app/pricing';
    }
    return Promise.reject(error);
  }
);

// Authentication API (simplified - no real auth)
export const authAPI = {
  register: (userData) => api.post('/auth/register', userData),
  login: (username, password) => {
    const formData = new URLSearchParams();
    formData.append('username', username);
    formData.append('password', password);
    return api.post('/auth/login', formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    });
  },
  getUsers: () => api.get('/auth/users'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/change-password', data),
};

// Sales API
export const salesAPI = {
  getAll: (params) => api.get('/sales', { params }),
  getById: (id) => api.get(`/sales/${id}`),
  create: (data) => api.post('/sales', data),
  update: (id, data) => api.put(`/sales/${id}`, data),
  delete: (id) => api.delete(`/sales/${id}`),
  batchUpdate: (data) => api.post('/sales/batch-update', data),
  getSummary: () => api.get('/sales/summary/stats'),
};

// Inventory API
export const inventoryAPI = {
  getAll: (params) => api.get('/inventory', { params }),
  getById: (id) => api.get(`/inventory/${id}`),
  create: (data) => api.post('/inventory', data),
  update: (id, data) => api.put(`/inventory/${id}`, data),
  delete: (id) => api.delete(`/inventory/${id}`),
  batchUpdate: (data) => api.post('/inventory/batch-update', data),
  getLowStockAlerts: () => api.get('/inventory/alerts/low-stock'),
  getSummary: () => api.get('/inventory/summary/stats'),
  adjustStock: (id, adjustment, reason) =>
    api.post(`/inventory/${id}/adjust-stock`, null, {
      params: { adjustment, reason }
    }),
};

// AI Analysis API
export const aiAPI = {
  analyze: (analysisParams) => api.post('/ai/analyze', analysisParams),
  getReports: (params) => api.get('/ai/reports', { params }),
  getReport: (id) => api.get(`/ai/reports/${id}`),
  getReportDetails: (id) => api.get(`/ai/reports/${id}`),
  downloadReport: (id) => api.get(`/ai/reports/${id}/download`, { responseType: 'blob' }),
  deleteReport: (id) => api.delete(`/ai/reports/${id}`),
  getQuickInsights: () => api.get('/ai/quick-insights'),
};

// File Operations API
export const filesAPI = {
  uploadSales: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/files/upload/sales', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadInventory: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/files/upload/inventory', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  exportData: (params) => api.post('/files/export', params, { responseType: 'blob' }),
  downloadTemplate: (sheetType) =>
    api.get(`/files/export/template/${sheetType}`, { responseType: 'blob' }),
};

// Scheduler API
export const schedulerAPI = {
  createTask: (data) => api.post('/scheduler/', data),
  getTasks: (params) => api.get('/scheduler', { params }),
  getTask: (id) => api.get(`/scheduler/${id}`),
  updateTask: (id, data) => api.put(`/scheduler/${id}`, data),
  deleteTask: (id) => api.delete(`/scheduler/${id}`),
  cancelTask: (id) => api.post(`/scheduler/${id}/cancel`),
  runTask: (id) => api.post(`/scheduler/${id}/run-now`),
  getStatus: () => api.get('/scheduler/status/summary'),
};

// System API
export const systemAPI = {
  getHealth: () => api.get('/health'),
  getStats: () => api.get('/stats'),
  getRoot: () => api.get('/'),
};

// Utility functions
export const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const formatError = (error) => {
  if (error.response?.data?.detail) {
    // Handle Pydantic validation errors (array of error objects)
    if (Array.isArray(error.response.data.detail)) {
      return error.response.data.detail
        .map(err => {
          const field = err.loc ? err.loc.join('.') : 'field';
          return `${field}: ${err.msg}`;
        })
        .join('; ');
    }
    // Handle string detail
    return error.response.data.detail;
  }
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

export const isTokenValid = () => {
  const token = localStorage.getItem('access_token');
  if (!token) return false;

  try {
    // Simple token validation - check if it's not expired
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
};

export default api;